import { Company } from "@domain/CRM/company";
import { StandardAxiosConfig, USER_NOT_LINKED } from "../../../common";
import type { User } from "@domain/Auth";
import type { SavedView } from "@domain/Settings";
import { DataAPI_Base, GetAPI_Base, UpdateAPI_Base } from "../base";
import { apiClient } from "../../../client";

class GetAPI_Companies extends GetAPI_Base<Company> {
	constructor() {
		super("companies", Company);
	}

	public async byUser(user: Pick<User, "companyIdsAll">) {
		if (user.companyIdsAll.length === 0) throw new Error(USER_NOT_LINKED);

		const allCompanies = await super.byIds(user.companyIdsAll);
		// `byIds` resolves via an OR-of-IDs filter, so Airtable returns the records
		// in view order — NOT the order of `companyIdsAll`. Detect the Rogo-AG admin
		// by scanning all results; checking only `allCompanies[0]` misfired whenever
		// Rogo AG didn't happen to sort first, silently skipping the admin list.
		if (allCompanies.some((company) => company.id && company.name.toLowerCase() === "rogo ag")) {
			// Rogo-AG (admin) users see every present or past customer, not just
			// companies with an active deal this season. `isActive` (Deals Active
			// rollup) catches brand-new customers whose first deal hasn't completed
			// work yet; `customerStatus` Current/Previous both require completed
			// work, so together they add dormant + past customers to the list.
			const activeCompanies = await super.byFilter({
				type: "OR",
				fields: [{ isActive: 1 }, { customerStatus: "Current" }, { customerStatus: "Previous" }],
			});
			allCompanies.push(...activeCompanies);
		}

		const uniqueCompanies = allCompanies.filter(
			(company, index, self) => index === self.findIndex((t) => t.id === company.id),
		);

		return uniqueCompanies;
	}
}

class UpdateAPI_Companies extends UpdateAPI_Base<Company> {
	constructor() {
		super("companies");
	}

	/** Admin-only: replaces the whole Portal Settings object (import columns + views). */
	public async portalSettings(id: string, value: any) {
		const url = `${this.baseUrl}/${id}/portal-settings`;
		await apiClient.post(url, value, StandardAxiosConfig());
	}

	/** Company members + Rogo staff: replaces only the Jobs Dashboard saved views. */
	public async portalViews(id: string, views: SavedView[]) {
		const url = `${this.baseUrl}/${id}/portal-views`;
		await apiClient.post(url, views, StandardAxiosConfig());
	}

	public async portalConfigurationOptions(id: string, value: string[]) {
		const url = `${this.baseUrl}/${id}/portal-configuration-options`;
		await apiClient.post(url, value, StandardAxiosConfig());
	}

	public async agrianOrganizationIds(id: string, value: string[]) {
		const url = `${this.baseUrl}/${id}/agrian-organization-id`;
		await apiClient.post(url, value, StandardAxiosConfig());
	}
}

export class CompaniesAPI extends DataAPI_Base<Company> {
	get: GetAPI_Companies;
	update: UpdateAPI_Companies;

	constructor() {
		super("companies", Company);
		this.get = new GetAPI_Companies();
		this.update = new UpdateAPI_Companies();
	}
}
